import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Carousel } from "primereact/carousel";
import { Skeleton } from "primereact/skeleton";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import ImagePreviewModal from "../../components/common/ImagePreviewModal";
import { AxiosError } from "axios";
import { errorHandler } from "../../utils/HelperFunctions";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

const MatriMonialView: React.FC = () => {
    const { id } = useParams();
    const initialized = useRef(false);
    const { showToast }: any = useToast();

    const [userdata, setUserdata] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    /* ================= IMAGE CAROUSEL ================= */
    const responsiveOptions = [
        { breakpoint: "1400px", numVisible: 4, numScroll: 1 },
        { breakpoint: "1199px", numVisible: 3, numScroll: 1 },
        { breakpoint: "767px", numVisible: 2, numScroll: 1 },
        { breakpoint: "575px", numVisible: 1, numScroll: 1 },
    ];

    const imageTemplate = (imagePath: string) => (
        <div className="p-carousel-item-content" style={{ textAlign: 'center' }}>
            <img
                src={imagePath}
                alt="Profile"
                style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    cursor: "pointer"
                }}
                onClick={() => setSelectedImage(imagePath)}
            />
        </div>
    );

    /* ================= API ================= */
    async function getUserData() {
        try {
            const res = await apiFunctions.get(
                `/maitrimonialmobile/mobile/getuserprofile/${id}`
            );

            if (res?.data?.success) {
                setUserdata(res.data.data);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: res.data.message,
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getUserData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) {
        return (
            <div className="dashboard-margin">
                <h2 className="card-title m-0">Profile</h2>

                <Card className="mt-3" title="Basic Info">
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-8">
                            {Array.from({ length: 15 }).map((_, i) => (
                                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                                    <div className="w-[180px]">
                                        <Skeleton height="1.25rem" className="rounded" />
                                    </div>
                                    <div className="flex-1">
                                        <Skeleton height="1.25rem" className="rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="col-span-12 md:col-span-4 flex justify-center">
                            <Skeleton shape="rectangle" width="200px" height="200px" className="rounded" />
                        </div>
                    </div>
                </Card>

                <Card className="mt-3" title="Photos">
                    <div className="flex gap-4 overflow-hidden">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} width="200px" height="150px" className="rounded" />
                        ))}
                    </div>
                </Card>

                {["Education Info", "Occupation Info", "Family Details"].map((title) => (
                    <Card className="mt-3" title={title} key={title}>
                        <Skeleton height="2rem" className="rounded mb-2" />
                        <Skeleton height="2rem" className="rounded" />
                    </Card>
                ))}
            </div>
        );
    }

    return (
        <>
            <div className="dashboard-margin">
                <h2 className="card-title m-0">Profile</h2>

                {/* ================= BASIC INFO ================= */}
                <Card className="mt-3" title="Basic Info">
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-8">
                            {[
                                ["Name", userdata?.fullname],
                                ["Gender", userdata?.gender],
                                ["DOB", userdata?.dobfull],
                                ["Age", userdata?.age],
                                ["Marital Status", userdata?.maritalstatus],
                                ["Blood Group", userdata?.bloodgroup],
                                ["Native Location", userdata?.nativeLocation],
                                ["Current Location", userdata?.currentLocation],
                                ["Home Address", userdata?.homeaddress],
                                ["Bio", userdata?.bio],
                                ["Birth Time", userdata?.birthtime],
                                ["Birth Place", userdata?.birthplace],
                                ["Rashi", userdata?.rashiShow || userdata?.rashi],
                                ["Nakshatra", userdata?.nakshatraShow || userdata?.nakshatra],
                                ["About", userdata?.about],
                                ["Height", `${userdata?.height} cm / ft`],
                                ["Weight", `${userdata?.weight} kg`],
                                ["Hobbies", userdata?.hobbies],
                                ["Mobile", userdata?.mobileno],
                                ["Annual Income", userdata?.annualincome],
                                ["Profile View count", userdata?.viewCount]
                            ].map(([label, value], i) => (
                                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                                    <span className="font-bold min-w-[180px]">
                                        {label}:
                                    </span>
                                    <span>{value || "-"}</span>
                                </div>
                            ))}
                            {[
                                ["House", userdata?.havehouse, userdata?.housedetails],
                                ["Plot", userdata?.haveplot, userdata?.plotdetails],
                                ["Agricultural Land", userdata?.haveagriland, `${userdata?.agrilanddetails} vigha/ acres`],
                                ["Shop", userdata?.haveshop, userdata?.shopdetails],
                                ["Factory", userdata?.havefactory, userdata?.factorydetails],
                                ["Commercial Space", userdata?.havecommerspace, userdata?.commerspacedetails]
                            ].map(([label, status, details], i) => (
                                <div key={i} className="mt-1">
                                    <div className="flex items-center gap-2">
                                        {status ? (
                                            <CheckCircleIcon className="w-6 h-6 text-green-600" />
                                        ) : (
                                            <XCircleIcon className="w-6 h-6 text-gray-400" />
                                        )}
                                        <span>{`Have ${label}`}</span>
                                    </div>
                                    {/* Show details only if status is true */}
                                    {status && details && (
                                        <div className="ml-8 text-gray-700 mt-1">
                                            <strong>{label} Details:</strong> {details}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="col-span-12 md:col-span-4 flex justify-center">
                            <img
                                src={userdata?.profileimgurl}
                                alt="Profile"
                                className="w-[200px] h-[200px] object-cover rounded cursor-pointer"
                                onClick={() =>
                                    setSelectedImage(userdata?.profileimgurl)
                                }
                            />
                        </div>
                    </div>
                </Card>

                {/* ================= PHOTOS ================= */}
                {userdata?.imagesUrl?.length > 0 && (
                    <Card className="mt-3" title="Photos">
                        <Carousel
                            value={userdata.imagesUrl}
                            itemTemplate={imageTemplate}
                            numVisible={4}
                            numScroll={1}
                            responsiveOptions={responsiveOptions}
                            showIndicators={userdata?.imagesUrl?.length > 4}
                            showNavigators={userdata?.imagesUrl?.length > 4}
                        />
                    </Card>
                )}

                {/* ================= EDUCATION ================= */}
                <Card className="mt-3" title="Education Info">
                    {userdata?.education?.length ? (
                        <Accordion multiple>
                            {userdata.education.map((edu: any) => (
                                <AccordionTab
                                    key={edu.id}
                                    header={`${edu.category} - ${edu.institute}`}
                                >
                                    <p><strong>Description:</strong> {edu.description}</p>
                                    <p><strong>Start:</strong> {edu.startdateshow} - {edu.enddateshow}</p>
                                    <p className="flex items-center gap-2"><strong>Is Purshing:</strong> {edu.ispursing ? <CheckCircleIcon className="w-6 h-6 text-green-600" /> : <XCircleIcon className="w-6 h-6 text-gray-400" />}</p>
                                    <p><strong>CGPA:</strong> {edu.cgpa}</p>
                                    <p><strong>Duration:</strong> {edu.gap}</p>
                                </AccordionTab>
                            ))}
                        </Accordion>
                    ) : (
                        <p>No education info available.</p>
                    )}
                </Card>

                {/* ================= OCCUPATION ================= */}
                <Card className="mt-3" title="Occupation Info">
                    {userdata?.occupation?.length ? (
                        <Accordion multiple>
                            {userdata.occupation.map((job: any) => (
                                <AccordionTab
                                    key={job.id}
                                    header={`${job.designation} - ${job.companyname}`}
                                >
                                    <p><strong>Company:</strong> {job.companyname}</p>
                                    <p><strong>Duration:</strong> {job.startdateshow} - {job.enddateshow}</p>
                                    <p><strong>Total Working (Years & Month):</strong> {job.gap}</p>
                                    <p><strong>Salary:</strong> {job.salary}</p>
                                </AccordionTab>
                            ))}
                        </Accordion>
                    ) : (
                        <p>No occupation info available.</p>
                    )}
                </Card>

                {/* ================= FAMILY DETAILS ================= */}
                <Card className="mt-3" title="Family Details">
                    {userdata?.familyDetails?.length ? (
                        <Accordion multiple>
                            {userdata.familyDetails.map((f: any) => (
                                <AccordionTab
                                    key={f.id}
                                    header={`${f.fullname} - ${f.relationship}`}
                                >
                                    <p><strong>Name:</strong> {f.fullname}</p>
                                    <p><strong>Relationship:</strong> {f.relationship}</p>
                                    <p><strong>Occupation:</strong> {f.occupation || "-"}</p>
                                    <p><strong>Age:</strong> {f.age || "-"}</p>
                                </AccordionTab>
                            ))}
                        </Accordion>
                    ) : (
                        <p>No family details available.</p>
                    )}
                </Card>
            </div>

            {/* ================= IMAGE PREVIEW ================= */}
            {selectedImage && (
                <ImagePreviewModal
                    selectedImage={selectedImage}
                    onclose={() => setSelectedImage(null)}
                />
            )}
        </>
    );
};

export default MatriMonialView;

// import React, { useEffect, useRef, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { Card } from "primereact/card";
// import { Accordion, AccordionTab } from "primereact/accordion";
// import { Carousel } from "primereact/carousel";
// import { InputText as PrimeInputText } from "primereact/inputtext";
// import { Dropdown } from "primereact/dropdown";
// import apiFunctions from "../../services/api-service";
// import useToast from "../../components/common/useToast";
// import ImagePreviewModal from "../../components/common/ImagePreviewModal";
// import { AxiosError } from "axios";
// import { errorHandler } from "../../utils/HelperFunctions";
// import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

// const MatriMonialView: React.FC = () => {
//     const { id } = useParams();
//     const navigate = useNavigate();
//     const initialized = useRef(false);
//     const { showToast }: any = useToast();

//     const [userdata, setUserdata] = useState<any>(null);
//     const [dataLoad, setDataLoad] = useState(true);
//     const [selectedImage, setSelectedImage] = useState<string | null>(null);
//     const [isEditing, setIsEditing] = useState(false);

//     const rashiOptions = [
//         { label: "Mesh", value: "Mesh" },
//         { label: "Vrishabha", value: "Vrishabha" },
//         { label: "Mithun", value: "Mithun" },
//         { label: "Karka", value: "Karka" },
//         { label: "Simha", value: "Simha" },
//         { label: "Kanya", value: "Kanya" },
//         { label: "Tula", value: "Tula" },
//         { label: "Vrischika", value: "Vrischika" },
//         { label: "Dhanu", value: "Dhanu" },
//         { label: "Makara", value: "Makara" },
//         { label: "Kumbha", value: "Kumbha" },
//         { label: "Meena", value: "Meena" },
//     ];

//     const editableFields = ["fullname", "rashi", "nakshatra", "height", "weight", "hobbies"]; // Only these are editable

//     /* ================= IMAGE CAROUSEL ================= */
//     const responsiveOptions = [
//         { breakpoint: "1400px", numVisible: 4, numScroll: 1 },
//         { breakpoint: "1199px", numVisible: 3, numScroll: 1 },
//         { breakpoint: "767px", numVisible: 2, numScroll: 1 },
//         { breakpoint: "575px", numVisible: 1, numScroll: 1 },
//     ];

//     const imageTemplate = (imagePath: string) => (
//         <div className="p-carousel-item-content" style={{ textAlign: 'center' }}>
//             <img
//                 src={imagePath}
//                 alt="Profile"
//                 style={{
//                     width: '100%',
//                     height: 'auto',
//                     maxHeight: '400px',
//                     objectFit: 'contain',
//                     cursor: "pointer"
//                 }}
//                 onClick={() => setSelectedImage(imagePath)}
//             />
//         </div>
//     );

//     /* ================= API ================= */
//     async function getUserData() {
//         try {
//             setDataLoad(true);
//             const res = await apiFunctions.get(
//                 `/maitrimonialmobile/mobile/getuserprofile/${id}`
//             );

//             if (res?.data?.success) {
//                 setUserdata(res.data.data);
//             } else {
//                 showToast({
//                     severity: "error",
//                     summary: "Error",
//                     detail: res.data.message,
//                     life: 3000,
//                 });
//             }
//         } catch (err) {
//             errorHandler(err as AxiosError);
//         } finally {
//             setDataLoad(false);
//         }
//     }

//     useEffect(() => {
//         if (!initialized.current) {
//             initialized.current = true;
//             getUserData();
//         }
//     }, [id]);

//     /* ================= UPDATE PROFILE API ================= */
//     const handleUpdateProfile = async () => {
//         try {
//             setDataLoad(true);
//             const res = await apiFunctions.put(
//                 `/maitrimonialmobile/mobile/updateuserprofile/${id}`,
//                 userdata
//             );
//             if (res?.data?.success) {
//                 showToast({ severity: "success", summary: "Success", detail: "Profile updated", life: 3000 });
//                 setIsEditing(false);
//             } else {
//                 showToast({ severity: "error", summary: "Error", detail: res.data.message, life: 3000 });
//             }
//         } catch (err) {
//             errorHandler(err as AxiosError);
//         } finally {
//             setDataLoad(false);
//         }
//     };

//     /* ================= RENDER FIELD ================= */
//     const renderField = (label: string, fieldKey: string, type: "text" | "dropdown" = "text", options?: any) => {
//         const value = userdata?.[fieldKey];
//         const editable = isEditing && editableFields.includes(fieldKey);
//         return (
//             <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
//                 <span className="font-bold min-w-[180px]">{label}:</span>
//                 <span className="flex-1">
//                     {editable ? (
//                         type === "dropdown" ? (
//                             <Dropdown
//                                 value={value}
//                                 options={options}
//                                 onChange={(e) => setUserdata({ ...userdata, [fieldKey]: e.value })}
//                                 placeholder={`Select ${label}`}
//                                 className="w-full"
//                             />
//                         ) : (
//                             <PrimeInputText
//                                 value={value}
//                                 onChange={(e) => setUserdata({ ...userdata, [fieldKey]: e.target.value })}
//                                 className="w-full"
//                             />
//                         )
//                     ) : (
//                         <span>{value || "-"}</span>
//                     )}
//                 </span>
//             </div>
//         );
//     };

//     return (
//         <>
//             <div className="dashboard-margin">
//                 <h2 className="card-title m-0">Profile</h2>

//                 {/* ================= BASIC INFO ================= */}
//                 <Card className="mt-3" title="Basic Info">
//                     <div className="flex justify-end mb-2">
//                         {isEditing && (<button
//                             className={`px-4 py-2 rounded bg-gray-600 hover:bg-gray-700 text-white mr-2`}
//                             onClick={() => {
//                                 setIsEditing(false);
//                             }}
//                         >
//                             Cancel
//                         </button>)}
//                         <button
//                             className={`px-4 py-2 rounded ${isEditing ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
//                             onClick={() => {
//                                 if (isEditing) {
//                                     handleUpdateProfile();
//                                 } else {
//                                     setIsEditing(true);
//                                 }
//                             }}
//                         >
//                             {isEditing ? "Update" : "Edit"}
//                         </button>
//                     </div>

//                     <div className="grid grid-cols-12 gap-4">
//                         <div className="col-span-12 md:col-span-8">
//                             {renderField("Name", "fullname")}
//                             {renderField("Gender", "gender")}
//                             {renderField("DOB", "dobfull")}
//                             {renderField("Age", "age")}
//                             {renderField("Marital Status", "maritalstatus")}
//                             {renderField("Blood Group", "bloodgroup")}
//                             {renderField("Native Location", "nativeLocation")}
//                             {renderField("Current Location", "currentLocation")}
//                             {renderField("Home Address", "homeaddress")}
//                             {renderField("Bio", "bio")}
//                             {renderField("Birth Time", "birthtime")}
//                             {renderField("Birth Place", "birthplace")}
//                             {renderField("Rashi", "rashi", "dropdown", rashiOptions)}
//                             {renderField("Nakshatra", "nakshatra")}
//                             {renderField("About", "about")}
//                             {renderField("Height", "height")}
//                             {renderField("Weight", "weight")}
//                             {renderField("Hobbies", "hobbies")}
//                             {renderField("Mobile", "mobileno")}
//                             {renderField("Annual Income", "annualincome")}
//                             {renderField("Profile View count", "viewCount")}
//                         </div>

//                         <div className="col-span-12 md:col-span-4 flex justify-center">
//                             <img
//                                 src={userdata?.profileimgurl}
//                                 alt="Profile"
//                                 className="w-[200px] h-[200px] object-cover rounded cursor-pointer"
//                                 onClick={() => setSelectedImage(userdata?.profileimgurl)}
//                             />
//                         </div>
//                     </div>
//                 </Card>

//                 {/* ================= PHOTOS ================= */}
//                 {userdata?.imagesUrl?.length > 0 && (
//                     <Card className="mt-3" title="Photos">
//                         <Carousel
//                             value={userdata.imagesUrl}
//                             itemTemplate={imageTemplate}
//                             numVisible={4}
//                             numScroll={1}
//                             responsiveOptions={responsiveOptions}
//                             showIndicators={userdata?.imagesUrl?.length > 4}
//                             showNavigators={userdata?.imagesUrl?.length > 4}
//                         />
//                     </Card>
//                 )}

//                 {/* ================= EDUCATION ================= */}
//                 <Card className="mt-3" title="Education Info">
//                     {userdata?.education?.length ? (
//                         <Accordion multiple>
//                             {userdata.education.map((edu: any) => (
//                                 <AccordionTab
//                                     key={edu.id}
//                                     header={`${edu.category} - ${edu.institute}`}
//                                 >
//                                     <p><strong>Description:</strong> {edu.description}</p>
//                                     <p><strong>Start:</strong> {edu.startdateshow} - {edu.enddateshow}</p>
//                                     <p className="flex items-center gap-2"><strong>Is Purshing:</strong> {edu.ispursing ? <CheckCircleIcon className="w-6 h-6 text-green-600" /> : <XCircleIcon className="w-6 h-6 text-gray-400" />}</p>
//                                     <p><strong>CGPA:</strong> {edu.cgpa}</p>
//                                     <p><strong>Duration:</strong> {edu.gap}</p>
//                                 </AccordionTab>
//                             ))}
//                         </Accordion>
//                     ) : (
//                         <p>No education info available.</p>
//                     )}
//                 </Card>

//                 {/* ================= OCCUPATION ================= */}
//                 <Card className="mt-3" title="Occupation Info">
//                     {userdata?.occupation?.length ? (
//                         <Accordion multiple>
//                             {userdata.occupation.map((job: any) => (
//                                 <AccordionTab
//                                     key={job.id}
//                                     header={`${job.designation} - ${job.companyname}`}
//                                 >
//                                     <p><strong>Company:</strong> {job.companyname}</p>
//                                     <p><strong>Duration:</strong> {job.startdateshow} - {job.enddateshow}</p>
//                                     <p><strong>Total Working (Years & Month):</strong> {job.gap}</p>
//                                     <p><strong>Salary:</strong> {job.salary}</p>
//                                 </AccordionTab>
//                             ))}
//                         </Accordion>
//                     ) : (
//                         <p>No occupation info available.</p>
//                     )}
//                 </Card>

//                 {/* ================= FAMILY DETAILS ================= */}
//                 <Card className="mt-3" title="Family Details">
//                     {userdata?.familyDetails?.length ? (
//                         <Accordion multiple>
//                             {userdata.familyDetails.map((f: any) => (
//                                 <AccordionTab
//                                     key={f.id}
//                                     header={`${f.fullname} - ${f.relationship}`}
//                                 >
//                                     <p><strong>Name:</strong> {f.fullname}</p>
//                                     <p><strong>Relationship:</strong> {f.relationship}</p>
//                                     <p><strong>Occupation:</strong> {f.occupation || "-"}</p>
//                                     <p><strong>Age:</strong> {f.age || "-"}</p>
//                                 </AccordionTab>
//                             ))}
//                         </Accordion>
//                     ) : (
//                         <p>No family details available.</p>
//                     )}
//                 </Card>
//             </div>

//             {/* ================= IMAGE PREVIEW ================= */}
//             {selectedImage && (
//                 <ImagePreviewModal
//                     selectedImage={selectedImage}
//                     onclose={() => setSelectedImage(null)}
//                 />
//             )}
//         </>
//     );
// };

// export default MatriMonialView;
